import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import classes from './styles/home.module.css';
import { getSubCategory } from '../../services/Static';
import menuIcon from "../../assets/icons/search.png";
import downIcon from "../../assets/icons/down.png";

// ── Structured data ──────────────────────────────────────────
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "FetchJobs",
  "url": "https://www.fetchjobs.co",
  "logo": {
    "@type": "ImageObject",
    "url": "https://www.fetchjobs.co/logo.png",
    "width": 200,
    "height": 60
  },
  "sameAs": [],                          // ← add your social profile URLs here
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "url": "https://www.fetchjobs.co/contact"
  }
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "FetchJobs",
  "url": "https://www.fetchjobs.co",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://www.fetchjobs.co/remote-jobs-usa/{search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "FetchJobs – Jobs That Match Your Skills",
  "description": "Connecting job seekers with meaningful remote opportunities across the US, UK, Germany and India.",
  "url": "https://www.fetchjobs.co",
  "isPartOf": { "@type": "WebSite", "url": "https://www.fetchjobs.co" },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.fetchjobs.co" }
    ]
  }
};

// ── Component ────────────────────────────────────────────────
const Home = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSuggestion, setIsSuggestion] = useState(false);
  const [type, setType] = useState("United States");
  const navigate = useNavigate();

  const handleMouseEnter = () => setIsOpen(true);
  const closeDropdown = () => setIsOpen(false);

  const [allKeywords, setAllKeywords] = useState([]);

  const handleSearchInputChange = (e) => {
    e.preventDefault();
    setSearchTerm(e.target.value);
    setIsSuggestion(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchInputClick(searchTerm);
  };

  const handleSearchInputClick = (word) => {
    const slug = word.toLowerCase().replace(/\s+/g, '-');
    if (type === "India")          navigate(`/remote-jobs-india/${slug}`);
    else if (type === "Germany")   navigate(`/remote-jobs-germany/${slug}`);
    else if (type === "United Kingdom") navigate(`/remote-jobs-uk/${slug}`);
    else if (type === "France") navigate(`/remote-jobs-france/${slug}`);
    else if (type === "Australia") navigate(`/remote-jobs-australia/${slug}`);
    else                           navigate(`/remote-jobs-usa/${slug}`);
    setSearchTerm(word);
    setIsSuggestion(false);
  };

  useEffect(() => {
    const fetchAllSubcategory = async () => {
      try {
        const data = await getSubCategory();
        setAllKeywords(data);
      } catch (err) {
        console.log("error ", err);
      }
    };
    fetchAllSubcategory();
  }, []);

  const filterSuggestedKeyword = (term) =>
    allKeywords?.filter((word) =>
      word.SubCategoryName.toLowerCase().startsWith(term.toLowerCase())
    );

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch("https://get.geojs.io/v1/ip/geo.json");
        const data = await response.json();
        if (data.country === "India")          setType("India");
        else if (data.country === "United Kingdom") setType("United Kingdom");
        else if (data.country === "Canada")    setType("Canada");
        else if (data.country === "Germany")   setType("Germany");
        else if (data.country === "France")    setType("France");
        else if (data.country === "Australia") setType("Australia");
        else                                   setType("United States");
      } catch {
        console.log("Error in IP to Country API");
      }
    };
    fetchCountry();
  }, []);

  return (
    <>
      {/* ── Technical SEO ── */}
      <Helmet>
        {/* Core */}
        <title>FetchJobs – Jobs That Match Your Skills</title>
        <meta name="description" content="Connecting job seekers with meaningful remote opportunities across the US, UK, Germany, Australia, France and India. Search thousands of jobs by title, skill or location." />
        <meta name="keywords" content="remote jobs, jobs in USA, jobs in India, jobs in UK, jobs in Germany, jobs in France, jobs in Australia, find jobs online" />

        {/* Canonical */}
        <link rel="canonical" href="https://www.fetchjobs.co/" />

        {/* Open Graph */}
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="FetchJobs – Jobs That Match Your Skills" />
        <meta property="og:description" content="Connecting job seekers with meaningful remote opportunities across the US, UK, Germany, Australia, France and India." />
        <meta property="og:url"         content="https://www.fetchjobs.co/" />
        <meta property="og:site_name"   content="FetchJobs" />
        <meta property="og:image"       content="https://www.fetchjobs.co/logo.png" />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary" />
        <meta name="twitter:title"       content="FetchJobs – Jobs That Match Your Skills" />
        <meta name="twitter:description" content="Connecting job seekers with meaningful remote opportunities across the US, UK, Germany, Australia, France and India." />
        <meta name="twitter:image"       content="https://www.fetchjobs.co/logo.png" />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Organization schema */}
        <script type="application/ld+json">
          {JSON.stringify(ORGANIZATION_SCHEMA)}
        </script>

        {/* WebSite schema — enables Google Sitelinks Searchbox */}
        <script type="application/ld+json">
          {JSON.stringify(WEBSITE_SCHEMA)}
        </script>

        {/* WebPage schema */}
        <script type="application/ld+json">
          {JSON.stringify(WEBPAGE_SCHEMA)}
        </script>
      </Helmet>

      <div className={classes.container}>
        <h1 className={classes.title}>Jobs That Match Your Skills</h1>
        <p className={classes.subTitle}>Connecting job seekers with meaningful opportunities</p>
        <div className={classes.innerContainer}>
          <div className={classes.searchCon}>
            <img src={menuIcon} alt="Find remote jobs" className={classes.searchIcon} />
            <input
              placeholder="Enter job title / designations / skill"
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyDown={handleKeyDown}
              className={classes.searchInput}
            />

            {searchTerm && isSuggestion && (
              <div className={classes.suggestCon}>
                {filterSuggestedKeyword(searchTerm).map((item, i) => (
                  <div key={i}>
                    <p
                      className={classes.suggestedKeyword}
                      onClick={() => handleSearchInputClick(item.SubCategoryName)}
                    >
                      {item.SubCategoryName}
                    </p>
                  </div>
                ))}
                <div key="keyword">
                  <p
                    className={classes.suggestedKeyword}
                    onClick={() => handleSearchInputClick(searchTerm)}
                  >
                    {searchTerm}
                  </p>
                </div>
              </div>
            )}

            <div className={classes.dropdown} onMouseEnter={handleMouseEnter} onMouseLeave={closeDropdown}>
              <button className={classes.dropdownButton} aria-expanded={isOpen}>
                {type} <img src={downIcon} alt="remote Jobs" />
              </button>
              {isOpen && (
                <div className={classes.dropdownMenu}>
                  <Link to="/remote-jobs-usa"     onClick={() => setType("United States")}>United States</Link>
                  <Link to="/remote-jobs-uk"      onClick={() => setType("United Kingdom")}>United Kingdom</Link>
                  <Link to="/remote-jobs-germany" onClick={() => setType("Germany")}>Germany</Link>
                  <Link to="/remote-jobs-france"  onClick={() => setType("France")}>France</Link>
                  <Link to="/remote-jobs-australia" onClick={() => setType("Australia")}>Australia</Link>
                  <Link to="/remote-jobs-india"   onClick={() => setType("India")}>India</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;